import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { usePageMeta } from '@/lib/use-document-meta'
import { AuthShell } from '@/components/AuthShell'
import { Field, inputClass, primaryButtonClass } from '@/components/ui'
import { probeRemote, unlockWithPassword, reconcile } from '@/tracker/sync'
import { useTracker } from '@/tracker/store'

export const Route = createFileRoute('/login')({ component: LoginPage })

function LoginPage() {
  usePageMeta({
    path: '/login',
    title: 'Sign in — Lunara',
    description: 'Sign in to Lunara to sync your private cycle data across devices (optional).',
    noindex: true,
  })
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await login.mutateAsync({ email, password })
      // Try to pull + reconcile the encrypted vault. Never block login on a
      // crypto failure — the user can unlock from the dashboard instead.
      try {
        const { hasRemoteVault } = await probeRemote()
        if (hasRemoteVault) {
          await unlockWithPassword(password)
          const result = await reconcile()
          if (result === 'imported-remote') await useTracker.getState().reload()
          // 'conflict' leaves status='conflict'; the dashboard card shows the modal.
        }
      } catch {
        toast('Enter your password on the dashboard to unlock sync.')
      }
      toast.success('Welcome back')
      navigate({ to: '/dashboard' })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Login failed')
    }
  }

  return (
    <AuthShell
      title="Sign in"
      subtitle="Sign in to sync across devices — end-to-end encrypted."
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="text-primary font-medium">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        <button type="submit" disabled={login.isPending} className={primaryButtonClass}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  )
}
